/**
 * PodPartner Custom Personalization Input
 *
 * Replaces PodPartner's broken fabric.js modal with a simple inline text input.
 * Data-driven: reads customization type, labels, and layer config from PodPartner's API.
 *
 * Two modes:
 *   - Primary: API works -> creates hidden inputs inside form for _layers + design text
 *   - Fallback: API broken -> creates plain properties[Song Name] for manual relay
 */
(function () {
  'use strict';

  var API_BASE = '/apps/proxy/pod/v1/shop/shopify/app/';

  var container = document.getElementById('podpartner-song-input');
  if (!container) return;

  var productId = container.dataset.productId;
  var sectionId = container.dataset.sectionId;
  if (!productId || !sectionId) return;

  // -- Helpers --

  function stripHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  }

  function findProductForm() {
    return document.getElementById('bpdp-form')
      || document.getElementById('product-form-' + sectionId);
  }

  // -- Neutralize PodPartner's external hidden inputs --
  // PodPartner's Vue app may create, re-create, or re-enable inputs with cached
  // old values. A MutationObserver strips the name attribute from any PodPartner
  // inputs so they can never contribute to FormData, regardless of timing.

  function neutralizePodPartnerInputs() {
    function stripNames() {
      document.querySelectorAll('input[name="properties[_layers]"]:not(#pp-own-layers)').forEach(function (el) {
        el.removeAttribute('name');
      });
      document.querySelectorAll('input[name="properties[Your customized design]"]:not(#pp-own-design)').forEach(function (el) {
        el.removeAttribute('name');
      });
    }

    // Strip any that already exist
    stripNames();

    // Watch for new ones PodPartner may create later
    var observer = new MutationObserver(stripNames);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['name'] });
  }

  // -- API fetch (cached in sessionStorage) --

  function fetchProductData() {
    var cacheKey = 'pp_product_' + productId;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var parsed = JSON.parse(cached);
        return Promise.resolve(parsed);
      }
    } catch (e) { /* ignore storage errors */ }

    return fetch(API_BASE + productId)
      .then(function (resp) {
        if (!resp.ok) throw new Error('API returned ' + resp.status);
        return resp.json();
      })
      .then(function (json) {
        if (
          !json.data ||
          !json.data.custom_product_list ||
          !json.data.custom_product_list[0] ||
          !json.data.custom_product_list[0].design_info ||
          !json.data.custom_product_list[0].design_info[0] ||
          !json.data.custom_product_list[0].design_info[0].layers
        ) {
          throw new Error('Unexpected API response shape');
        }
        try { sessionStorage.setItem(cacheKey, JSON.stringify(json.data)); } catch (e) {}
        return json.data;
      });
  }

  function parseLayersFromApi(data) {
    var designInfo = data.custom_product_list[0].design_info[0];
    var layers = JSON.parse(designInfo.layers);
    var customizable = layers.filter(function (l) { return l.customize_id; });
    var textLayers = customizable.filter(function (l) { return l.type === 'text'; });

    // DEBUG: dump text layer properties to help diagnose alignment issues
    console.group('[BMYJOY] PodPartner text layer properties');
    textLayers.forEach(function (l, i) {
      console.log('Layer ' + i + ':', JSON.parse(JSON.stringify(l)));
    });
    console.groupEnd();

    return {
      allLayers: layers,
      textLayers: textLayers,
      imageLayers: customizable.filter(function (l) { return l.type === 'image'; }),
      designInfo: designInfo
    };
  }

  // -- Hidden inputs inside the form --

  function createFormInputs(form) {
    var layersInput = document.createElement('input');
    layersInput.type = 'hidden';
    layersInput.name = 'properties[_layers]';
    layersInput.id = 'pp-own-layers';
    layersInput.value = '';
    form.appendChild(layersInput);

    var designInput = document.createElement('input');
    designInput.type = 'hidden';
    designInput.name = 'properties[Your customized design]';
    designInput.id = 'pp-own-design';
    designInput.value = '';
    form.appendChild(designInput);

    return { layers: layersInput, design: designInput };
  }

  // -- UI rendering --

  function renderUI(parsed, personalization, ownInputs) {
    var title = personalization.product_title || personalization.modal_title || 'Personalize';
    var hint = stripHtml(personalization.modal_text || personalization.product_default_button_text || '');

    var html = '<div class="pp-custom-input">';

    if (hint) {
      html += '<p class="pp-custom-input__hint">' + hint + '</p>';
    }

    if (parsed.textLayers.length > 0) {
      parsed.textLayers.forEach(function (layer, i) {
        var position = layer.customize || '';
        var songNum = i + 1;
        var label = 'Song ' + songNum;
        var inputId = 'pp-text-input-' + i;
        html += '<div class="pp-custom-input__field">';
        html += '<label class="pp-custom-input__label" for="' + inputId + '">' + label + '</label>';
        html += '<input type="text" id="' + inputId + '" class="pp-custom-input__input" placeholder="e.g. I Love My Computer :3" data-layer-index="' + i + '" maxlength="50" autocomplete="off">';
        html += '</div>';
      });
    }

    if (parsed.imageLayers.length > 0 && parsed.textLayers.length === 0) {
      html += '<p class="pp-custom-input__note">Image customization is available — please contact us to customize.</p>';
    }

    html += '</div>';

    container.innerHTML = html;
    container.style.display = '';

    if (parsed.textLayers.length === 0 && parsed.imageLayers.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Bind input events — update our own hidden inputs inside the form
    var inputs = container.querySelectorAll('.pp-custom-input__input');
    inputs.forEach(function (input) {
      input.addEventListener('input', function () {
        updateDesign(parsed, ownInputs, inputs);
      });
    });
  }

  function updateDesign(parsed, ownInputs, inputs) {
    var updatedLayers = JSON.parse(JSON.stringify(parsed.allLayers));
    var designTexts = [];

    inputs.forEach(function (input) {
      var idx = parseInt(input.dataset.layerIndex, 10);
      var textLayer = parsed.textLayers[idx];
      var value = input.value.trim();

      for (var i = 0; i < updatedLayers.length; i++) {
        if (updatedLayers[i].customize_id === textLayer.customize_id) {
          updatedLayers[i].text = value || textLayer.text;
          updatedLayers[i].customize = value || '';
          updatedLayers[i].customize_used = !!value;
          break;
        }
      }

      if (value) designTexts.push(value);
    });

    ownInputs.layers.value = JSON.stringify(updatedLayers);
    ownInputs.design.value = designTexts.join(', ');
  }

  function renderFallbackUI() {
    console.warn('[BMYJOY] PodPartner integration changed — using fallback mode');

    var html = '<div class="pp-custom-input">';
    html += '<p class="pp-custom-input__title">Personalize</p>';
    html += '<p class="pp-custom-input__hint">Add your song name</p>';
    html += '<div class="pp-custom-input__field">';
    html += '<input type="text" id="pp-fallback-input" class="pp-custom-input__input" placeholder="your song" maxlength="50" autocomplete="off">';
    html += '</div>';
    html += '<p class="pp-custom-input__note">Customization will be confirmed via email</p>';
    html += '</div>';

    container.innerHTML = html;
    container.style.display = '';

    var form = findProductForm();
    if (form) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[Song Name]';
      hidden.id = 'pp-fallback-hidden';
      hidden.value = '';
      form.appendChild(hidden);

      var input = document.getElementById('pp-fallback-input');
      input.addEventListener('input', function () {
        hidden.value = input.value.trim();
      });
    }
  }

  // -- Init --

  async function init() {
    var apiResult = null;
    var parsed = null;

    try {
      apiResult = await fetchProductData();
    } catch (err) {
      console.warn('[BMYJOY] PodPartner API check failed:', err.message);
      renderFallbackUI();
      return;
    }

    try {
      parsed = parseLayersFromApi(apiResult);
    } catch (err) {
      console.warn('[BMYJOY] PodPartner layer parsing failed:', err.message);
      renderFallbackUI();
      return;
    }

    if (parsed.textLayers.length === 0 && parsed.imageLayers.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Create our own hidden inputs inside the product form immediately
    var form = findProductForm();
    if (!form) {
      renderFallbackUI();
      return;
    }

    var ownInputs = createFormInputs(form);
    var personalization = apiResult.personalization || {};
    renderUI(parsed, personalization, ownInputs);

    // Neutralize PodPartner's external inputs so only ours are in FormData
    neutralizePodPartnerInputs();
  }

  init();
})();
